import classNames from 'classnames'
import { FieldAttributes, FormikProps, getIn, useFormikContext } from 'formik'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { Input } from '@ui/components/Input/Input'
import { HideIcon } from '@ui/icons/Hide.icon'
import { ShowIcon } from '@ui/icons/Show.icon'

import { removeRestrictedSpecialCharacters } from '@utils/validations'

import { getError, getLabel, getPlaceholder } from './helpers'

export interface FormFieldProps<FormValues>
  extends Partial<FormikProps<FormValues>>,
    FieldAttributes<any> {
  namePrefix?: string
  hint?: string | React.ReactNode
  name: string
  label?: string
  className?: string
  showLabel?: boolean
  disabled?: boolean
  required?: boolean
  onChange?(value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void
  type?: string
  error?: string
  placeholder?: string
  hidePlaceholderAsterisk?: boolean
  autoComplete?: string
}

export function RawFormField<FormValues>(props: FormFieldProps<FormValues>): React.JSX.Element {
  const {
    namePrefix = '',
    name,
    type,
    hint,
    hidePlaceholderAsterisk,
    autoComplete,
    onChange,
    ...inputProps
  } = props

  const fieldName = namePrefix + name
  const fieldRef = useRef<HTMLDivElement>(null)
  const context = useFormikContext<FormValues>()

  const error = getError(context.errors, props.error, name)
  const touched = getIn(context.touched, name)

  const [inputType, setInputType] = useState(type)

  const label = getLabel(hidePlaceholderAsterisk ? { ...props, required: false } : props)

  const placeholder = getPlaceholder(
    hidePlaceholderAsterisk ? { ...props, required: false } : props
  )

  const labelProps = useMemo(
    () => (label ? { label, placeholder } : { label: undefined, placeholder: undefined }),
    [label, placeholder]
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(event)
      } else {
        context.setFieldValue(fieldName, removeRestrictedSpecialCharacters(event.target.value))
      }
    },
    [onChange, context, fieldName]
  )

  const handleBlur = useCallback(() => {
    context.setFieldTouched(fieldName, true)
  }, [context, fieldName])

  const onEndIconClick = useCallback(
    () => setInputType(() => (inputType === 'password' ? 'text' : 'password')),
    [inputType]
  )

  const showHint = touched && error ? error : hint
  const showError = !!error && !!touched

  return (
    <div className={classNames('control')} ref={fieldRef}>
      <Input
        name={fieldName}
        {...inputProps}
        {...labelProps}
        type={inputType}
        autoComplete={autoComplete}
        onChange={handleChange}
        onBlur={handleBlur}
        error={showError}
        hint={showHint ? hint : undefined}
        rightIcon={inputType === 'password' ? HideIcon : ShowIcon}
        onEndIconClick={onEndIconClick}
      />
    </div>
  )
}

export function createFormPasswordField<V>(): React.ComponentType<FormFieldProps<V>> {
  return RawFormField
}
